/// Contract record. Mirrors the `contracts` table.
export type ContractStatus = "draft" | "active" | "canceled" | "expired";
export type ContractPipeline = "new" | "renewal" | "upsell";
export type LpCountType = "LA" | "LP";
export type ContractLineItemRevenueType = "mrr" | "spot";
/** 契約一覧のアーカイブフィルタ。既定は exclude (アーカイブ済みを除外)。 */
export type ContractArchiveFilter = "exclude" | "only" | "include";

export const CONTRACT_STATUS_LABELS: Record<ContractStatus, string> = {
  draft: "下書き",
  active: "有効",
  canceled: "解約",
  expired: "期限切れ",
};

export const CONTRACT_PIPELINE_LABELS: Record<ContractPipeline, string> = {
  new: "新規",
  renewal: "更新",
  upsell: "アップセル",
};

export const LP_COUNT_TYPE_LABELS: Record<LpCountType, string> = {
  LA: "LA",
  LP: "LP",
};

export const CONTRACT_LINE_ITEM_REVENUE_TYPE_LABELS: Record<
  ContractLineItemRevenueType,
  string
> = {
  mrr: "MRR",
  spot: "SPOT",
};

/// Contract のレスポンスモデル (`Contract` / `ContractArchiveHistoryEntry` /
/// `Paged<Contract>`) は #471 で `domain/contract/model.ts` (branded) へ移設。
/// API レスポンスは `parseContract` を通すこと。

export interface ContractListQuery {
  firm_id?: string;
  lawyer_id?: string;
  opportunity_id?: string;
  status?: ContractStatus;
  /** アーカイブ済みの扱い。未指定 = exclude (除外)。 */
  archive_filter?: ContractArchiveFilter;
  sort_by?: "status" | "signed_on" | "ends_on" | "created_at" | "updated_at";
  sort_dir?: "asc" | "desc";
  page?: number;
  page_size?: number;
}

export interface ContractCreateRequest {
  firm_id: string;
  opportunity_id?: string | null;
  deal_name?: string | null;
  pipeline?: ContractPipeline | null;
  deal_stage?: string | null;
  forecast_category?: string | null;
  agency_name?: string | null;
  lp_count_type?: LpCountType | null;
  contract_type?: string | null;
  is_individual_contract?: boolean;
  /** 担当者 (assignee, multi; ACC-4)。app_accounts(id) の配列。 */
  assignee_account_ids?: string[];
  primary_lawyer_id?: string | null;
  related_lawyer_ids?: string[] | null;
  main_interest_lawyer_id?: string | null;
  individual_contract_memo?: string | null;
  auto_renewal?: boolean;
  cancellation_notice_period_days?: number | null;
  customer_contract_owner?: string | null;
  active_user_count?: number | null;
  monthly_amount?: string | null;
  mrr_manual?: string | null;
  initial_amount?: string | null;
  annual_contract_amount?: string | null;
  proposed_acv?: string | null;
  proposed_mrr?: string | null;
  spot_amount?: string | null;
  signed_on?: string | null;
  starts_on?: string | null;
  ends_on?: string | null;
  mrr_recognition_start_month?: string | null;
  mrr_recognition_end_month?: string | null;
  billing_frequency?: string | null;
  billing_name?: string | null;
  billing_email?: string | null;
  payment_terms?: string | null;
  payment_method?: string | null;
  billing_irregular?: boolean;
  pro_billing_memo?: string | null;
  pro_billing_method?: string | null;
  pro_billing_email?: string | null;
  pro_billing_postal_code?: string | null;
  pro_billing_address_line?: string | null;
  pro_billing_building?: string | null;
  pro_payment_due?: string | null;
  memo?: string | null;
  contract_document_url?: string | null;
  renewal_likelihood_score?: string | null;
  line_items?: ContractCreateLineItemRequest[];
}

export interface ContractPatchRequest {
  firm_id?: string;
  opportunity_id?: string | null;
  deal_name?: string | null;
  pipeline?: ContractPipeline | null;
  deal_stage?: string | null;
  forecast_category?: string | null;
  agency_name?: string | null;
  lp_count_type?: LpCountType | null;
  contract_type?: string | null;
  is_individual_contract?: boolean;
  /** 担当者 (assignee, multi; ACC-4)。app_accounts(id) の配列。 */
  assignee_account_ids?: string[];
  primary_lawyer_id?: string | null;
  related_lawyer_ids?: string[] | null;
  main_interest_lawyer_id?: string | null;
  individual_contract_memo?: string | null;
  auto_renewal?: boolean;
  cancellation_notice_period_days?: number | null;
  customer_contract_owner?: string | null;
  active_user_count?: number | null;
  monthly_amount?: string | null;
  mrr_manual?: string | null;
  initial_amount?: string | null;
  annual_contract_amount?: string | null;
  proposed_acv?: string | null;
  proposed_mrr?: string | null;
  spot_amount?: string | null;
  signed_on?: string | null;
  starts_on?: string | null;
  ends_on?: string | null;
  mrr_recognition_start_month?: string | null;
  mrr_recognition_end_month?: string | null;
  billing_frequency?: string | null;
  billing_name?: string | null;
  billing_email?: string | null;
  payment_terms?: string | null;
  payment_method?: string | null;
  billing_irregular?: boolean;
  pro_billing_memo?: string | null;
  pro_billing_method?: string | null;
  pro_billing_email?: string | null;
  pro_billing_postal_code?: string | null;
  pro_billing_address_line?: string | null;
  pro_billing_building?: string | null;
  pro_payment_due?: string | null;
  memo?: string | null;
  contract_document_url?: string | null;
  renewal_likelihood_score?: string | null;
}

export interface TransitionContractRequest {
  next_status: ContractStatus;
  reason?: string | null;
}

export interface ArchiveContractRequest {
  /** アーカイブ理由 (必須)。 */
  reason: string;
}

export interface RestoreContractRequest {
  /** 復元理由 (任意)。 */
  reason?: string | null;
}

export interface ContractLineItem {
  id: string;
  contract_id: string;
  firm_id: string;
  product_id: string | null;
  name: string;
  module_type: string | null;
  revenue_type: ContractLineItemRevenueType | null;
  acceptance_status: string | null;
  schedule_start_on: string | null;
  schedule_end_on: string | null;
  recognition_month: string | null;
  proposed_amount_ex_tax: string | null;
  split_count: number | null;
  sort_order: number;
  schedule_revenue_total: string | null;
  schedules: ContractRevenueSchedule[];
  created_at: string;
  updated_at: string;
}

export interface ContractRevenueSchedule {
  id: string;
  line_item_id: string;
  firm_id: string;
  schedule_date: string;
  revenue: string | null;
  billing_amount: string | null;
  created_at: string;
  updated_at: string;
}

export interface ContractRevenueScheduleList {
  items: ContractRevenueSchedule[];
}

export interface ContractRevenueScheduleCreateRequest {
  schedule_date: string;
  revenue?: string | null;
  billing_amount?: string | null;
}

export interface ContractRevenueSchedulePatchRequest {
  schedule_date?: string;
  revenue?: string | null;
  billing_amount?: string | null;
}

export interface ContractLineItemList {
  items: ContractLineItem[];
}

export interface ContractLineItemCreateRequest {
  product_id?: string | null;
  name: string;
  module_type?: string | null;
  revenue_type?: ContractLineItemRevenueType | null;
  acceptance_status?: string | null;
  schedule_start_on?: string | null;
  schedule_end_on?: string | null;
  recognition_month?: string | null;
  proposed_amount_ex_tax?: string | null;
  split_count?: number | null;
  sort_order?: number;
}

export interface ContractLineItemPatchRequest {
  product_id?: string | null;
  name?: string;
  module_type?: string | null;
  revenue_type?: ContractLineItemRevenueType | null;
  acceptance_status?: string | null;
  schedule_start_on?: string | null;
  schedule_end_on?: string | null;
  recognition_month?: string | null;
  proposed_amount_ex_tax?: string | null;
  split_count?: number | null;
  sort_order?: number;
}

export interface ContractCreateLineItemRequest
  extends ContractLineItemCreateRequest {
  schedules?: ContractRevenueScheduleCreateRequest[];
}
